import type { CampaignPurchase } from "@/lib/enem-campaign";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", cache: "no-store", ...init });
  const payload = await response.json().catch(() => ({})) as T & { detail?: string; code?: string };
  if (!response.ok) throw new Error(payload.code || payload.detail || "BILLING_REQUEST_FAILED");
  return payload;
}

export type BillingSummary = {
  asOf: string;
  allowedCountries: string[];
  prices: Array<{ country: string; currency: string; unitAmountMinor: number; recurrence: "MONTHLY" }>;
  trialDays: number;
  trialEligible: boolean;
  trialActive: boolean;
  lifetimeAccess?: boolean;
  trialStartsAt?: string | null;
  trialEndsAt?: string | null;
  subscription?: { status: string; currentPeriodEnd?: string | null; cancelAtPeriodEnd: boolean } | null;
  campaignPurchases?: CampaignPurchase[];
  invoices: Array<{ invoiceId: string; status: string; amountReceivedMinor: number; currency: string; accessState: string }>;
};

export type ReferralLink = {
  ok: boolean;
  referralCode: string;
  referralUrl: string;
};

export type ReferralInvitee = {
  userId: string;
  email: string;
  acceptedAt: string;
  status: "TRIAL" | "PAYING" | "REGISTERED";
  creditGeneratedMicroUsd: number;
};

export type ReferralInvitees = {
  ok: boolean;
  currency: "USD";
  unit: "micro-USD";
  invitees: ReferralInvitee[];
};

export const billingKeys = {
  summary: ["billing", "summary"] as const,
  credits: ["billing", "credits"] as const,
  referralLink: (locale: string) => ["billing", "referral-link", locale] as const,
  referralInvitees: ["billing", "referral-invitees"] as const,
};

export function useBillingSummary(enabled = true) {
  return useQuery({ queryKey: billingKeys.summary, queryFn: () => request<BillingSummary>("/api/v1/billing/summary"), enabled, staleTime: 15_000 });
}

export function useReferralCredits() {
  return useQuery({ queryKey: billingKeys.credits, queryFn: () => request<{ available: number; pending: number; debt: number }>("/api/v1/referrals/credits"), staleTime: 15_000 });
}

export function useReferralLink(locale: string) {
  return useQuery({
    queryKey: billingKeys.referralLink(locale),
    queryFn: () => request<ReferralLink>(`/api/v1/referrals/me?locale=${encodeURIComponent(locale)}`),
    staleTime: 5 * 60_000,
  });
}

export function useReferralInvitees() {
  return useQuery({
    queryKey: billingKeys.referralInvitees,
    queryFn: () => request<ReferralInvitees>("/api/v1/referrals/invitees"),
    staleTime: 15_000,
  });
}

export function useCreateCheckout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ country }: { country: string }) => request<{ url: string }>("/api/v1/billing/checkout-sessions", {
      method: "POST",
      headers: { "content-type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ country }),
    }),
    onSuccess: () => client.invalidateQueries({ queryKey: billingKeys.summary }),
  });
}
